import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  ArrowLeft, Calendar, Trash2, Plus, Check, X,
  Users as UsersIcon, Sparkles, Layers, ClipboardCheck, StickyNote, Loader2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import UserAvatar from '../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLOR_MAP = {
  sky:     { bar: 'bg-sky-500',     tint: 'bg-sky-50',     border: 'border-sky-200',     dot: 'text-sky-600' },
  emerald: { bar: 'bg-emerald-500', tint: 'bg-emerald-50', border: 'border-emerald-200', dot: 'text-emerald-600' },
  violet:  { bar: 'bg-violet-500',  tint: 'bg-violet-50',  border: 'border-violet-200',  dot: 'text-violet-600' },
  rose:    { bar: 'bg-rose-500',    tint: 'bg-rose-50',    border: 'border-rose-200',    dot: 'text-rose-600' },
  amber:   { bar: 'bg-amber-500',   tint: 'bg-amber-50',   border: 'border-amber-200',   dot: 'text-amber-600' },
  slate:   { bar: 'bg-slate-500',   tint: 'bg-slate-100',  border: 'border-slate-200',   dot: 'text-slate-600' },
};

const STATUS_LABEL = (status, t) => t(`projects.status.${status}`);

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [addingOpen, setAddingOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const locale = i18n.language === 'de' ? de : enUS;

  const fetchProject = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/projects/${id}`, { headers: getAuthHeader() });
      setProject(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
      navigate('/projects');
    }
  }, [id, getAuthHeader, t, navigate]);

  const fetchTasks = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/projects/${id}/tasks`, { headers: getAuthHeader() });
      setTasks(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  }, [id, getAuthHeader, t]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(r.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchProject(), fetchTasks(), fetchUsers()]).finally(() => setLoading(false));
  }, [fetchProject, fetchTasks, fetchUsers]);

  useEffect(() => {
    const sync = () => { if (!document.hidden) fetchTasks(); };
    const interval = setInterval(sync, 15000);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [fetchTasks]);

  const color = COLOR_MAP[project?.color] || COLOR_MAP.sky;

  const groupedTasks = useMemo(() => {
    const templateTasks = tasks.filter(t => t.template_id);
    const adhocTasks = tasks.filter(t => !t.template_id);
    return {
      template: templateTasks,
      adhoc: adhocTasks,
      done: tasks.filter(t => t.completed).length,
      total: tasks.length,
    };
  }, [tasks]);

  const toggleComplete = async (task) => {
    try {
      await axios.put(`${API_URL}/projects/${id}/tasks/${task.id}`, {
        completed: !task.completed,
      }, { headers: getAuthHeader() });
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const updateAssignee = async (task, assigneeId) => {
    try {
      const body = assigneeId === '__clear__'
        ? { clear_assignee: true }
        : { assignee_id: assigneeId };
      await axios.put(`${API_URL}/projects/${id}/tasks/${task.id}`, body, { headers: getAuthHeader() });
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const updateDue = async (task, date) => {
    try {
      const body = date ? { due_date: date } : { clear_due_date: true };
      await axios.put(`${API_URL}/projects/${id}/tasks/${task.id}`, body, { headers: getAuthHeader() });
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const saveTitle = async (task) => {
    if (!editTitle.trim()) { toast.error(t('projects.taskTitleRequired')); return; }
    try {
      await axios.put(`${API_URL}/projects/${id}/tasks/${task.id}`, {
        title: editTitle.trim(),
      }, { headers: getAuthHeader() });
      setEditingTaskId(null);
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(t('projects.confirmDeleteTask'))) return;
    try {
      await axios.delete(`${API_URL}/projects/${id}/tasks/${task.id}`, { headers: getAuthHeader() });
      toast.success(t('projects.taskDeleted'));
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const addTask = async () => {
    if (!newTitle.trim()) { toast.error(t('projects.taskTitleRequired')); return; }
    try {
      await axios.post(`${API_URL}/projects/${id}/tasks`, {
        title: newTitle.trim(),
        order: tasks.length,
      }, { headers: getAuthHeader() });
      setNewTitle('');
      setAddingOpen(false);
      toast.success(t('projects.taskCreated'));
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const canDeleteProject = !!project && (project.created_by === user?.id || user?.role === 'admin');

  const handleDeleteProject = async () => {
    if (!window.confirm(t('projects.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/projects/${id}`, { headers: getAuthHeader() });
      toast.success(t('projects.deleted'));
      navigate('/projects');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  if (loading || !project) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">{t('common.loading')}</div>
      </Layout>
    );
  }

  const progressPct = groupedTasks.total > 0 ? Math.round((groupedTasks.done / groupedTasks.total) * 100) : 0;

  return (
    <Layout>
      <div data-testid="project-detail-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="rounded-full"
            data-testid="project-detail-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-sm text-zinc-500 flex-1">{t('projects.backToList')}</span>
          {canDeleteProject && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeleteProject}
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
              data-testid="project-detail-delete"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {t('common.delete')}
            </Button>
          )}
        </div>

        {/* Project card */}
        <div className={`rounded-2xl border ${color.border} ${color.tint} overflow-hidden`}>
          <div className={`h-1.5 ${color.bar}`} />
          <div className="p-5 sm:p-6 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 bg-white/70 px-2 py-0.5 rounded-full">
                    {STATUS_LABEL(project.computed_status, t)}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {format(parseISO(project.start_date), 'd. MMM', { locale })} – {format(parseISO(project.end_date), 'd. MMM yyyy', { locale })}
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {project.title}
                </h1>
                {project.description && (
                  <p className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap max-w-3xl">
                    {project.description}
                  </p>
                )}
              </div>
            </div>

            {/* Progress */}
            <div>
              <div className="flex items-center justify-between text-xs text-zinc-600 mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <ClipboardCheck className="w-3.5 h-3.5" />
                  {groupedTasks.done} / {groupedTasks.total} {t('projects.tasksDone')}
                </span>
                <span className="font-semibold">{progressPct}%</span>
              </div>
              <div className="h-1.5 bg-white/70 rounded-full overflow-hidden">
                <div
                  className={`h-full ${color.bar} transition-all duration-500`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            {(project.lead_user_ids || []).length > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <UsersIcon className="w-3.5 h-3.5 text-zinc-500" />
                <div className="flex items-center gap-1.5 flex-wrap">
                  {project.lead_user_ids.map(uid => {
                    const u = users.find(x => x.id === uid) || { id: uid, name: '?' };
                    return (
                      <div key={u.id} className="flex items-center gap-1.5 bg-white/70 rounded-full px-2 py-0.5">
                        <UserAvatar user={u} size="xs" />
                        <span className="text-xs text-zinc-700">{u.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes area — running remarks / deviations */}
        <NotesEditor projectId={id} initialNotes={project.notes} getAuthHeader={getAuthHeader} t={t} onSaved={fetchProject} />

        {/* Tasks section */}
        <div className="space-y-5">
          <TaskGroup
            icon={Layers}
            title={t('projects.templateTasks')}
            hint={t('projects.templateTasksHint')}
            tasks={groupedTasks.template}
            users={users}
            locale={locale}
            t={t}
            currentUserId={user?.id}
            toggleComplete={toggleComplete}
            updateAssignee={updateAssignee}
            updateDue={updateDue}
            editingTaskId={editingTaskId}
            setEditingTaskId={setEditingTaskId}
            editTitle={editTitle}
            setEditTitle={setEditTitle}
            saveTitle={saveTitle}
            deleteTask={deleteTask}
          />

          <TaskGroup
            icon={Sparkles}
            title={t('projects.adhocTasks')}
            hint={t('projects.adhocTasksHint')}
            tasks={groupedTasks.adhoc}
            users={users}
            locale={locale}
            t={t}
            currentUserId={user?.id}
            toggleComplete={toggleComplete}
            updateAssignee={updateAssignee}
            updateDue={updateDue}
            editingTaskId={editingTaskId}
            setEditingTaskId={setEditingTaskId}
            editTitle={editTitle}
            setEditTitle={setEditTitle}
            saveTitle={saveTitle}
            deleteTask={deleteTask}
          />

          {/* Add ad-hoc task */}
          {addingOpen ? (
            <div className="border-2 border-[#D93025]/30 rounded-xl p-3 bg-red-50/30 flex items-center gap-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                placeholder={t('projects.newTaskPh')}
                className="h-10 flex-1"
                autoFocus
                data-testid="new-task-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => { setAddingOpen(false); setNewTitle(''); }}
                className="rounded-full h-9"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                onClick={addTask}
                className="rounded-full h-9 bg-[#D93025] hover:bg-[#B91C1C] text-white"
                data-testid="new-task-save"
              >
                <Check className="w-4 h-4 mr-1" /> {t('common.save')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddingOpen(true)}
              className="w-full rounded-xl border-dashed border-2 border-zinc-200 h-12 text-zinc-500 hover:border-[#D93025] hover:text-[#D93025]"
              data-testid="add-task-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t('projects.addTask')}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

const fmtEuro = (n) => `€ ${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Notes editor — free-text project notes with debounced auto-save
const NotesEditor = ({ projectId, initialNotes, getAuthHeader, t, onSaved }) => {
  const [value, setValue] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const timerRef = React.useRef(null);
  const lastServerRef = React.useRef(initialNotes || '');

  useEffect(() => {
    // If project reloads from parent with different notes, sync
    if ((initialNotes || '') !== lastServerRef.current) {
      lastServerRef.current = initialNotes || '';
      setValue(initialNotes || '');
      setDirty(false);
    }
  }, [initialNotes]);

  const save = React.useCallback(async (payload) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/projects/${projectId}`, { notes: payload }, { headers: getAuthHeader() });
      lastServerRef.current = payload;
      setSavedAt(new Date());
      setDirty(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.notesSaveError'));
    } finally {
      setSaving(false);
    }
  }, [projectId, getAuthHeader, t, onSaved]);

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    setDirty(v !== lastServerRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (v !== lastServerRef.current) {
      timerRef.current = setTimeout(() => save(v), 1200);  // debounced auto-save
    }
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (value !== lastServerRef.current) save(value);
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5" data-testid="project-notes-section">
      <div className="flex items-center gap-2 mb-3">
        <StickyNote className="w-4 h-4 text-zinc-500" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t('projects.notesTitle')}</h2>
        <span className="flex-1" />
        {saving && (
          <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> {t('projects.notesSaving')}
          </span>
        )}
        {!saving && dirty && (
          <span className="text-[11px] text-amber-600">{t('projects.notesUnsaved')}</span>
        )}
        {!saving && !dirty && savedAt && (
          <span className="text-[11px] text-emerald-600">{t('projects.notesSaved')}</span>
        )}
      </div>
      <Textarea
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={t('projects.notesPh')}
        rows={5}
        className="resize-y text-sm min-h-[120px]"
        data-testid="project-notes-textarea"
      />
      <p className="text-[11px] text-zinc-400 mt-2">{t('projects.notesHint')}</p>
    </section>
  );
};

const TaskGroup = ({
  icon: Icon, title, hint, tasks, users, locale, t, currentUserId,
  toggleComplete, updateAssignee, updateDue,
  editingTaskId, setEditingTaskId, editTitle, setEditTitle, saveTitle, deleteTask,
}) => {
  if (tasks.length === 0) return null;
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-zinc-500" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-700">{title}</h2>
        <span className="text-xs text-zinc-400">({tasks.length})</span>
      </div>
      {hint && <p className="text-xs text-zinc-500 mb-2">{hint}</p>}
      <div className="space-y-2">
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            users={users}
            locale={locale}
            t={t}
            currentUserId={currentUserId}
            toggleComplete={toggleComplete}
            updateAssignee={updateAssignee}
            updateDue={updateDue}
            editing={editingTaskId === task.id}
            beginEdit={() => { setEditingTaskId(task.id); setEditTitle(task.title); }}
            cancelEdit={() => setEditingTaskId(null)}
            editTitle={editTitle}
            setEditTitle={setEditTitle}
            saveTitle={saveTitle}
            deleteTask={deleteTask}
          />
        ))}
      </div>
    </section>
  );
};

const TaskRow = ({
  task, users, locale, t,
  toggleComplete, updateAssignee, updateDue,
  editing, beginEdit, cancelEdit, editTitle, setEditTitle, saveTitle, deleteTask,
}) => {
  return (
    <div
      className={`bg-white border border-zinc-200 rounded-xl p-3 flex items-center gap-3 transition-all ${task.completed ? 'opacity-60' : 'hover:border-zinc-300'}`}
      data-testid={`project-task-${task.id}`}
    >
      {/* Checkbox */}
      <button
        type="button"
        onClick={() => toggleComplete(task)}
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
          task.completed
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : 'border-zinc-300 hover:border-emerald-500'
        }`}
        data-testid={`toggle-task-${task.id}`}
        title={task.completed ? t('projects.markOpen') : t('projects.markDone')}
      >
        {task.completed && <Check className="w-3 h-3" strokeWidth={3} />}
      </button>

      {/* Title */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveTitle(task)}
              className="h-8"
              autoFocus
              data-testid={`edit-title-${task.id}`}
            />
            <button type="button" onClick={cancelEdit} className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100">
              <X className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => saveTitle(task)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50" data-testid={`save-title-${task.id}`}>
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={beginEdit}
            className={`text-sm text-left w-full truncate ${task.completed ? 'line-through text-zinc-400' : 'text-zinc-800 hover:text-zinc-900'}`}
          >
            {task.title}
          </button>
        )}
      </div>

      {/* Due date */}
      <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
        <Calendar className="w-3.5 h-3.5 text-zinc-400" />
        <input
          type="date"
          value={task.due_date || ''}
          onChange={(e) => updateDue(task, e.target.value)}
          className="text-xs text-zinc-600 border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-zinc-300 rounded px-1 py-0.5 w-32"
          data-testid={`due-date-${task.id}`}
        />
      </div>

      {/* Assignee */}
      <div className="flex-shrink-0 w-40">
        <Select
          value={task.assignee_id || '__none__'}
          onValueChange={(v) => updateAssignee(task, v === '__none__' ? '__clear__' : v)}
        >
          <SelectTrigger className="h-8 border-0 bg-transparent hover:bg-zinc-50 text-xs" data-testid={`assignee-${task.id}`}>
            {task.assignee_id ? (
              <span className="inline-flex items-center gap-1.5 truncate">
                <UserAvatar user={{ id: task.assignee_id, name: task.assignee_name || '?' }} size="xs" />
                <span className="truncate text-zinc-700">{task.assignee_name || '?'}</span>
              </span>
            ) : (
              <SelectValue placeholder={t('projects.assign')} />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t('projects.unassigned')}</SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Delete */}
      <button
        type="button"
        onClick={() => deleteTask(task)}
        className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
        data-testid={`delete-task-${task.id}`}
        title={t('common.delete')}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default ProjectDetailPage;
